import React from 'react';
import { Box, Flex, Icon, IconProps, Text } from 'pounceV2';
import { Theme, useTheme } from '@mui/material';
import { DetectionDiff } from './diffUtils';

interface DiffHunkCardProps {
  hunkData: DetectionDiff['hunks'][0];
}

type HunkType = DiffHunkCardProps['hunkData']['type'];
type LineType = 'added' | 'removed' | 'context';

interface DiffLineProps {
  lineNum: number;
  symbol: string;
  content: string;
  type: LineType;
}

const CHANGE_TYPE_CONFIG: Record<
  HunkType,
  { icon: IconProps['type']; label: string; color: (tokens: Theme['palette']['tokens']) => string }
> = {
  add: {
    icon: 'plus',
    label: 'ADDED',
    color: tokens => tokens.content.text.semantic.success.base,
  },
  remove: {
    icon: 'minus',
    label: 'REMOVED',
    color: tokens => tokens.content.text.semantic.error.base,
  },
  modify: {
    icon: 'edit',
    label: 'MODIFIED',
    color: tokens => tokens.content.text.semantic.suggest.base,
  },
};

const DEFAULT_CHANGE_TYPE = {
  icon: 'info' as IconProps['type'],
  label: 'CHANGED',
  color: (tokens: Theme['palette']['tokens']) => tokens.content.text.neutral[3],
};

const getLineStyles = (type: LineType, tokens: Theme['palette']['tokens']) => {
  switch (type) {
    case 'added':
      return {
        numBg: tokens.background.semantic.success[4],
        contentBg: tokens.background.semantic.success[5],
        textColor: tokens.content.text.code.meta.attributeName.class.base,
      };
    case 'removed':
      return {
        numBg: tokens.background.semantic.error[4],
        contentBg: tokens.background.semantic.error[5],
        textColor: tokens.content.text.semantic.warning.base,
      };
    default:
      return {
        numBg: 'transparent',
        contentBg: 'transparent',
        textColor: tokens.content.text.neutral[1],
      };
  }
};

const DiffLine: React.FC<DiffLineProps> = ({ lineNum, symbol, content, type }) => {
  const {
    palette: { tokens },
  } = useTheme();
  const styles = getLineStyles(type, tokens);
  return (
    <Flex align="center" justify="center" height={18} width="100%" minWidth="min-content">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        justifyContent="center"
        px="10px"
        flexShrink={0}
        width={34}
        backgroundColor={styles.numBg}
      >
        <Text
          fontFamily='"DM Mono", monospace'
          fontSize={14}
          lineHeight="18px"
          color={tokens.content.text.neutral[1]}
          textAlign="right"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {lineNum}
        </Text>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        width={24}
        backgroundColor={styles.contentBg}
      >
        <Text
          fontFamily='"DM Mono", monospace'
          fontSize={14}
          lineHeight="18px"
          color={styles.textColor}
          textAlign="center"
          whiteSpace="nowrap"
          width="100%"
        >
          {symbol}
        </Text>
      </Box>
      <Box
        display="flex"
        flex="1 0 0"
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="center"
        px="10px"
        minWidth={0}
        minHeight="1px"
        backgroundColor={styles.contentBg}
      >
        <Text
          fontFamily='"DM Mono", monospace'
          fontSize={14}
          lineHeight="18px"
          color={styles.textColor}
          whiteSpace="nowrap"
        >
          {content}
        </Text>
      </Box>
    </Flex>
  );
};

interface ParsedLine {
  lineNum: number;
  symbol: string;
  content: string;
  type: LineType;
}

const parseLines = (lines: string[], oldStart: number, newStart: number): ParsedLine[] => {
  let oldLine = oldStart;
  let newLine = newStart;

  return lines.map(line => {
    const prefix = line[0];
    const content = line.substring(1);

    if (prefix === '+') {
      const lineNum = newLine;
      newLine += 1;
      return { lineNum, symbol: '+', content, type: 'added' as const };
    }

    if (prefix === '-') {
      const lineNum = oldLine;
      oldLine += 1;
      return { lineNum, symbol: '-', content, type: 'removed' as const };
    }

    const lineNum = oldLine;
    oldLine += 1;
    newLine += 1;
    return { lineNum, symbol: ' ', content, type: 'context' as const };
  });
};

const DiffHunkCardV3 = ({ hunkData }: DiffHunkCardProps) => {
  const {
    palette: { tokens },
  } = useTheme();
  const { hunk, type } = hunkData;

  const filteredLines = React.useMemo(
    () => hunk.lines.filter(line => !line.includes('\\ No newline at end of file')),
    [hunk.lines],
  );

  const parsedLines = React.useMemo(
    () => parseLines(filteredLines, hunk.oldStart, hunk.newStart),
    [filteredLines, hunk.oldStart, hunk.newStart],
  );

  const config = CHANGE_TYPE_CONFIG[type] || DEFAULT_CHANGE_TYPE;
  const color = config.color(tokens);
  const lineRange = `Lines ${hunk.oldStart}-${hunk.oldStart + hunk.oldLines - 1}`;

  return (
    <Box display="flex" flexDirection="column" gap={2} p={3}>
      <Flex align="center" gap={2}>
        <Icon type={config.icon} size="small" color={color} />
        <Text variant="body/sm" fontWeight="bold" color={color}>
          {config.label}
        </Text>
        <Text variant="body/md" color={tokens.content.text.neutral[3]}>
          {lineRange}
        </Text>
      </Flex>
      <Box
        backgroundColor={tokens.background.neutral.inverse.raised[1]}
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        py={2}
        width="100%"
        overflowX="auto"
        borderRadius="large"
        borderWidth="1px"
        borderStyle="solid"
        borderColor={tokens.stroke.neutral[5]}
      >
        {parsedLines.map(line => (
          <DiffLine
            key={`${hunkData.id}-${line.type}-${line.lineNum}`}
            lineNum={line.lineNum}
            symbol={line.symbol}
            content={line.content}
            type={line.type}
          />
        ))}
      </Box>
    </Box>
  );
};

export default DiffHunkCardV3;
